import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PinInput } from './PinInput';
import { usePinManagement } from '@/hooks/usePinManagement';
import { useUser } from '@clerk/clerk-react';
import { Lock, Mail, CheckCircle, ArrowLeft } from 'lucide-react';
import { GlassDialog, GlassDialogContent, GlassDialogHeader, GlassDialogFooter } from '@/components/ui/glass-dialog';
import { GlassButton } from '@/components/ui/glass-button';

interface PinChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  forced?: boolean;
}

type Step = 'verify' | 'change' | 'email' | 'success' | 'reset-email' | 'reset-pin';

export const PinChangeDialog = ({ open, onOpenChange, forced = false }: PinChangeDialogProps) => {
  const { user } = useUser();
  const { requestPinChange, confirmPinChange, requestPinReset, confirmPinReset, isLoading } = usePinManagement();
  
  const [step, setStep] = useState<Step>('verify');
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const resetDialog = () => {
    setStep('verify');
    setCurrentPin('');
    setNewPin('');
    setConfirmNewPin('');
    setVerificationCode('');
    setError('');
    setUserEmail('');
  };

  const handleClose = () => {
    // Prevent closing if in forced mode, unless PIN change is complete (success step)
    if (forced && step !== 'success') {
      return;
    }
    resetDialog();
    onOpenChange(false);
  };

  const handleVerifyCurrentPin = () => {
    if (currentPin.length !== 6) {
      setError('Current PIN must be 6 digits');
      return;
    }
    setError('');
    setStep('change');
  };

  const handleRequestChange = async () => {
    if (newPin.length !== 6) {
      setError('New PIN must be 6 digits');
      return;
    }

    if (newPin !== confirmNewPin) {
      setError('New PINs do not match');
      return;
    }

    if (newPin === currentPin) {
      setError('New PIN must be different from current PIN');
      return;
    }

    if (!user?.emailAddresses[0]?.emailAddress) {
      setError('Email address not found');
      return;
    }

    setError('');
    const result = await requestPinChange(
      currentPin,
      newPin,
      confirmNewPin,
      user.emailAddresses[0].emailAddress
    );

    if (result.success) {
      setStep('email');
    } else {
      setError(result.error || 'Failed to request PIN change');
    }
  };

  const handleConfirmChange = async () => {
    if (verificationCode.length !== 6) {
      setError('Verification code must be 6 digits');
      return;
    }

    setError('');
    const result = await confirmPinChange(verificationCode);

    if (result.success) {
      setStep('success');
    } else {
      setError(result.error || 'Invalid verification code');
    }
  };

  const handleRequestReset = async () => {
    if (!userEmail.trim()) {
      setError('Please enter your email address');
      return;
    }

    const result = await requestPinReset(userEmail);
    if (result.success) {
      setStep('reset-pin');
      setError('');
    } else {
      setError(result.error || 'Failed to send reset code');
    }
  };

  const handleConfirmReset = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }
    if (!newPin.trim() || !confirmNewPin.trim()) {
      setError('Please enter and confirm your new PIN');
      return;
    }
    if (newPin !== confirmNewPin) {
      setError('PIN confirmation does not match');
      return;
    }

    const result = await confirmPinReset(verificationCode, newPin, confirmNewPin);
    if (result.success) {
      setStep('success');
      setError('');
    } else {
      setError(result.error || 'Failed to reset PIN');
    }
  };

  const renderStep = () => {
    switch (step) {
      case 'verify':
        return (
          <GlassDialogContent>
            <GlassDialogHeader
              icon={<Lock className="w-6 h-6 text-white/80" />}
              title="Verify Current PIN"
              description="Enter your current PIN to continue"
            />
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <PinInput
                value={currentPin}
                onChange={setCurrentPin}
                disabled={isLoading}
                error={!!error}
                validateStrength={false}
                className="justify-center"
              />
            </motion.div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="backdrop-blur-md bg-red-500/10 border border-red-400/20 rounded-xl px-4 py-3"
                >
                  <p className="text-red-300 text-sm font-light text-center">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {!forced && (
              <div className="text-center mt-4">
                <button
                  onClick={() => {
                    setStep('reset-email');
                    setUserEmail(user?.emailAddresses[0]?.emailAddress || '');
                  }}
                  disabled={isLoading}
                  className="text-orange-400 hover:text-orange-300 text-sm underline transition-colors"
                >
                  Forgot your PIN?
                </button>
              </div>
            )}
          </GlassDialogContent>
        );

      case 'change':
        return (
          <GlassDialogContent>
            <GlassDialogHeader
              icon={<Lock className="w-6 h-6 text-white/80" />}
              title="Set New PIN"
              description="Choose a new 6-digit PIN"
            />
            
            <div className="space-y-5">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.15 }}
              >
                <Label className="text-sm font-inter font-light text-white/70 mb-3 block tracking-wide">
                  New PIN
                </Label>
                <PinInput
                  value={newPin}
                  onChange={setNewPin}
                  disabled={isLoading}
                  error={!!error}
                  className="justify-center"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <Label className="text-sm font-inter font-light text-white/70 mb-3 block tracking-wide">
                  Confirm New PIN
                </Label>
                <PinInput
                  value={confirmNewPin}
                  onChange={setConfirmNewPin}
                  disabled={isLoading}
                  error={!!error}
                  className="justify-center"
                />
              </motion.div>
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="backdrop-blur-md bg-red-500/10 border border-red-400/20 rounded-xl px-4 py-3"
                >
                  <p className="text-red-300 text-sm font-light text-center">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </GlassDialogContent>
        );

      case 'email':
        return (
          <GlassDialogContent>
            <GlassDialogHeader
              icon={<Mail className="w-6 h-6 text-white/80" />}
              title="Check Your Email"
              description={`We've sent a verification code to ${user?.emailAddresses[0]?.emailAddress}`}
            />
            
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <Label htmlFor="verification-code" className="text-sm font-inter font-light text-white/70 mb-3 block tracking-wide">
                Verification Code
              </Label>
              <Input
                id="verification-code"
                type="text"
                placeholder="Enter 6-digit code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-lg font-mono backdrop-blur-md bg-white/10 border-white/20 text-white/95 placeholder:text-white/40 focus:bg-white/15 focus:border-white/30 rounded-xl h-12"
                disabled={isLoading}
              />
            </motion.div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="backdrop-blur-md bg-red-500/10 border border-red-400/20 rounded-xl px-4 py-3"
                >
                  <p className="text-red-300 text-sm font-light text-center">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </GlassDialogContent>
        );

      case 'reset-email':
        return (
          <GlassDialogContent>
            <GlassDialogHeader
              icon="🔐"
              title="Reset Your PIN"
              description="Enter your email address to receive a verification code"
            />
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Email Address</label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={isLoading}
                  className="w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-400/50"
                />
              </div>
            </div>

            {error && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 p-3 rounded-lg bg-red-500/20 border border-red-400/30 text-red-300 text-sm"
                >
                  {error}
                </motion.div>
              </AnimatePresence>
            )}
          </GlassDialogContent>
        );

      case 'reset-pin':
        return (
          <GlassDialogContent>
            <GlassDialogHeader
              icon="🔐"
              title="Reset Your PIN"
              description="Enter the verification code from your email and choose a new PIN"
            />
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Verification Code</label>
                <PinInput
                  value={verificationCode}
                  onChange={setVerificationCode}
                  error={!!error}
                  disabled={isLoading}
                  validateStrength={false}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">New PIN</label>
                <PinInput
                  value={newPin}
                  onChange={setNewPin}
                  error={!!error}
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/80 mb-2">Confirm New PIN</label>
                <PinInput
                  value={confirmNewPin}
                  onChange={setConfirmNewPin}
                  error={!!error}
                  disabled={isLoading}
                />
              </div>
            </div>

            {error && (
              <AnimatePresence>
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 p-3 rounded-lg bg-red-500/20 border border-red-400/30 text-red-300 text-sm"
                >
                  {error}
                </motion.div>
              </AnimatePresence>
            )}
          </GlassDialogContent>
        );

      case 'success':
        return (
          <GlassDialogContent>
            <motion.div
              className="text-center space-y-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="flex justify-center">
                <motion.div
                  className="p-4 rounded-3xl backdrop-blur-md bg-green-500/20 border border-green-400/30"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
                >
                  <CheckCircle className="w-12 h-12 text-green-400" />
                </motion.div>
              </div>
              <div>
                <h3 className="text-xl font-inter font-light text-white/95 mb-3 tracking-wide">
                  PIN Changed Successfully!
                </h3>
                <p className="text-sm text-white/60 font-inter font-light tracking-wide leading-relaxed">
                  Your PIN has been updated. You can now use your new PIN to unlock Open Insurance.
                </p>
              </div>
            </motion.div>
          </GlassDialogContent>
        );

      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (step) {
      case 'verify':
        return (
          <GlassDialogFooter>
            <div className="flex gap-3">
              <GlassButton 
                variant="secondary" 
                onClick={handleClose} 
                disabled={isLoading}
                className="flex-1"
              >
                Cancel
              </GlassButton>
              <GlassButton 
                variant="primary"
                onClick={handleVerifyCurrentPin}
                disabled={currentPin.length !== 6 || isLoading}
                loading={isLoading}
                className="flex-1"
              >
                Continue
              </GlassButton>
            </div>
          </GlassDialogFooter>
        );

      case 'change':
        return (
          <GlassDialogFooter>
            <div className="flex gap-3">
              <GlassButton 
                variant="ghost" 
                onClick={() => setStep('verify')} 
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </GlassButton>
              <GlassButton 
                variant="primary"
                onClick={handleRequestChange}
                disabled={newPin.length !== 6 || confirmNewPin.length !== 6 || isLoading}
                loading={isLoading}
                className="flex-1"
              >
                Send Verification Code
              </GlassButton>
            </div>
          </GlassDialogFooter>
        );

      case 'email':
        return (
          <GlassDialogFooter>
            <div className="flex gap-3">
              <GlassButton 
                variant="ghost" 
                onClick={() => setStep('change')} 
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </GlassButton>
              <GlassButton 
                variant="primary"
                onClick={handleConfirmChange}
                disabled={verificationCode.length !== 6 || isLoading}
                loading={isLoading}
                className="flex-1"
              >
                Confirm Change
              </GlassButton>
            </div>
          </GlassDialogFooter>
        );

      case 'reset-email':
        return (
          <GlassDialogFooter>
            <div className="flex gap-3 w-full">
              <GlassButton
                variant="secondary"
                onClick={() => setStep('verify')}
                disabled={isLoading}
                className="flex-1"
              >
                Back
              </GlassButton>
              <GlassButton
                onClick={handleRequestReset}
                disabled={isLoading || !userEmail.trim()}
                loading={isLoading}
                className="flex-1"
              >
                Send Reset Code
              </GlassButton>
            </div>
          </GlassDialogFooter>
        );

      case 'reset-pin':
        return (
          <GlassDialogFooter>
            <div className="flex gap-3 w-full">
              <GlassButton
                variant="secondary"
                onClick={() => setStep('reset-email')}
                disabled={isLoading}
                className="flex-1"
              >
                Back
              </GlassButton>
              <GlassButton
                onClick={handleConfirmReset}
                disabled={isLoading || !verificationCode.trim() || !newPin.trim() || !confirmNewPin.trim()}
                loading={isLoading}
                className="flex-1"
              >
                Reset PIN
              </GlassButton>
            </div>
          </GlassDialogFooter>
        );

      case 'success':
        return (
          <GlassDialogFooter>
            <GlassButton 
              variant="primary" 
              onClick={handleClose}
              className="w-full"
            >
              Done
            </GlassButton>
          </GlassDialogFooter>
        );

      default:
        return null;
    }
  };

  return (
    <GlassDialog 
      open={open} 
      onOpenChange={forced ? undefined : onOpenChange}
      hideCloseButton={forced}
      className="max-w-lg"
    >
      <div className="space-y-6">
        {/* Status indicator for forced updates */}
        {forced && (
          <motion.div
            className="text-center mb-2"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="inline-flex items-center px-3 py-1 rounded-full backdrop-blur-md bg-brand-orange/20 border border-brand-orange/30">
              <div className="w-2 h-2 bg-brand-orange rounded-full mr-2 animate-pulse" />
              <span className="text-xs font-inter font-light text-brand-orange-light tracking-wide">
                Security Update Required
              </span>
            </div>
          </motion.div>
        )}

        {renderStep()}
        {renderFooter()}
      </div>
    </GlassDialog>
  );
};